/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 */

/**
 * Helper to do checks to confirm if combined calculations in compare table are valid or not.
 * In a few applicable cases, valid combined calculation results can be queried for.  In other cases, it
 * checks the following:
 *	- whether there is overlap in data covered in the compare card visualizations
 *  - whether the category context in the compare card visualizations is the same
 * if there is data overlap, there is a risk of double counting, so combined calculations should not be shown
 * if category Contexts are different, then comparison is not apples to apples, so combined calculations should not be shown
 */
class WidgetContextChecker {
	constructor(widgetContentApisMap) {
		this.widgetContentApisMap = widgetContentApisMap;
	}

	process(newCompareTableStatsEnabled, isSelectionStats=false) {
		const isCategoryContextSame = this.checkIfCategoryContextIsSame();

		return this.queryForCombinedSummariesIfApplicable(newCompareTableStatsEnabled, isSelectionStats).then((combinedSummaryResponse) => {
			if (combinedSummaryResponse && !combinedSummaryResponse.hasUnprocessedItems) {
				const canCombineCalculations = combinedSummaryResponse.measures !== null;
				return { isCategoryContextSame, canCombineCalculations, combinedSummaryResponse };
			}
			return this.checkIfSummariesCanBeCombined().then( ( canCombineCalculations ) => {
				return { isCategoryContextSame,  canCombineCalculations, combinedSummaryResponse };
			});
		});
	}

	checkIfSummariesCanBeCombined() {
		const widgetContentApis = Object.values(this.widgetContentApisMap);
		if (widgetContentApis.length === 2) {
			const summarizer = widgetContentApis[0].getFeature('WidgetAPI.deprecated').getFeature('vis-summarizer');

			const combinedSummaryValidator=summarizer && summarizer.getCombinedSummaryValidator();
			combinedSummaryValidator && combinedSummaryValidator.combineSummariesWith(widgetContentApis[1].getFeature('WidgetAPI.deprecated'));

			return combinedSummaryValidator.isInitialized() && combinedSummaryValidator.validate().then((testResult) => {
				return testResult;
			});
		}
		return Promise.resolve(false);
	}

	/**
	 * Some combined calculations can't be determined by adding the values of the two individual charts
	 * BUT they can be determined using a single row query.  This handler passes the widgetId's to the summarizer
	 * and, if it determines a query can be done to find some common, combined measure results, it will perform it to get the values of all applicable measures.
	 * It also returns a flag stating whether there are more common measures to be handled for the regular overlap test or not.
	 * NOTE: currently, this query is only done for countdistinct usecases but could be extended to other aggregation types in future where data overlaps.
	 * @param {boolean} newCompareTableStatsEnabled - We never query unless the new calculations flag is true.
	 * @returns A promise whose value is null if no query is helpful or a queryResult with the set of measures and their values and the status flag.
	 */
	queryForCombinedSummariesIfApplicable(newCompareTableStatsEnabled, isSelectionStats) {
		if (!newCompareTableStatsEnabled) {
			return Promise.resolve(null);
		}

		const widgetContentApis = Object.values(this.widgetContentApisMap);
		if (widgetContentApis.length === 2) {
			const summarizer = widgetContentApis[0].getFeature('WidgetAPI.deprecated').getFeature('vis-summarizer');

			const widgetApis = widgetContentApis.map(widgetContentApi => widgetContentApi.getFeature('WidgetAPI.deprecated'));
			const combinedSummaryValueQuery = summarizer && summarizer.createCombinedSummaryValueQuery(widgetApis, isSelectionStats);
			if (!combinedSummaryValueQuery) {
				return Promise.resolve(null);
			}

			return combinedSummaryValueQuery.execute().then((valueQueryResult) => {
				const summaryTableJSON = valueQueryResult && valueQueryResult.getSummaryTableJSON();
				return {
					measures: summaryTableJSON && summaryTableJSON.measures,
					hasUnprocessedItems: combinedSummaryValueQuery.hasUnprocessedItems()
				};
			});
		}
		return Promise.resolve(null);
	}

	checkIfCategoryContextIsSame() {
		const widgetContentApis = Object.values(this.widgetContentApisMap);
		const categoryContexts = {};

		widgetContentApis.forEach( (widgetContentApi) => {
			const widgetId = widgetContentApi.getId();

			const visApi = widgetContentApi.getFeature('Visualization');
			if(visApi) {
				let items = visApi.getSlots().getMappingInfoList().filter(mappingInfo => {
					return mappingInfo && mappingInfo.slot && mappingInfo.slot.getDefinition().getType() ==='category';
				});
				items = items.map((dataItemInfo) => dataItemInfo.dataItem.getId());
				items.sort();
				categoryContexts[widgetId] = items.join(',');
			}
		} );

		let foundMismatch = false;

		let last = null;
		Object.values( categoryContexts ).forEach( ( categoryContext ) => {
			if( !last ) {
				last = categoryContext;
			} else {
				if( categoryContext && last !== categoryContext ) {
					foundMismatch = true;
				}
			}
		});

		return !foundMismatch;
	}
}

export default WidgetContextChecker;



// WEBPACK FOOTER //
// ./src/js/features/compare/WidgetContextChecker.js